export type BillingState =
  | 'trialing'
  | 'trial_expired'
  | 'active'
  | 'past_due'
  | 'canceling'
  | 'free';

export interface WorkspaceBillingState {
  plan?: string | null;
  status?: string | null;
  activeTrial?: boolean;
  trialCompleted?: boolean;
  renew?: boolean;
}

const paidPlans = ['monthly', 'yearly', 'lifetime'];
const paymentRequiredStatuses = [
  'pending',
  'past_due',
  'past-due',
  'unpaid',
  'failed',
  'payment_failed',
];

export const deriveBillingState = (
  billing?: WorkspaceBillingState | null,
): BillingState => {
  if (billing?.activeTrial && !billing?.trialCompleted) return 'trialing';
  if (billing?.activeTrial && billing?.trialCompleted) return 'trial_expired';
  if (paymentRequiredStatuses.includes(billing?.status?.toLowerCase() || '')) {
    return 'past_due';
  }

  if (billing?.plan && paidPlans.includes(billing.plan)) {
    return billing.renew === false ? 'canceling' : 'active';
  }

  return 'free';
};

export const hasStandardBillingAccess = (
  billing?: WorkspaceBillingState | null,
) => {
  const state = deriveBillingState(billing);
  return state === 'trialing' || state === 'active' || state === 'canceling';
};

export const isPaymentRequired = (billing?: WorkspaceBillingState | null) =>
  deriveBillingState(billing) === 'past_due';

export const isSubscriptionExpired = (billing?: WorkspaceBillingState | null) =>
  billing?.plan === 'free_tier' && deriveBillingState(billing) === 'free';

export type AccessRecoveryNotice =
  | 'trial_ended'
  | 'subscription_ended'
  | 'payment_required'
  | null;

export const getAccessRecoveryNotice = (
  billing?: WorkspaceBillingState | null,
): AccessRecoveryNotice => {
  if (isPaymentRequired(billing)) return 'payment_required';
  if (deriveBillingState(billing) === 'trial_expired') return 'trial_ended';
  if (!isSubscriptionExpired(billing)) return null;

  const status = billing?.status?.toLowerCase();
  return status === 'canceled' || status === 'cancelled'
    ? 'subscription_ended'
    : 'trial_ended';
};
